export const ARCA_MOZZO_REPRESENTATIVE_CUIT = "20340938071";
export const ARCA_MOZZO_REPRESENTATIVE_CUIT_DISPLAY = "20-34093807-1";
export const ARCA_RELATION_MANAGER_URL =
  "https://auth.afip.gob.ar/contribuyente_/login.xhtml";

export const ARCA_ENVIRONMENTS = Object.freeze([
  {
    value: "production",
    label: "Produccion",
    description: "Emite comprobantes fiscales reales cuando la conexion sea aprobada.",
  },
  {
    value: "homologation",
    label: "Homologacion",
    description: "Solo sirve para pruebas tecnicas y no genera comprobantes validos.",
  },
]);

export const ARCA_IVA_CONDITIONS = Object.freeze([
  { id: 1, label: "IVA Responsable Inscripto" },
  { id: 4, label: "IVA Sujeto Exento" },
  { id: 6, label: "Responsable Monotributo" },
  { id: 13, label: "Monotributista Social" },
  { id: 15, label: "IVA No Alcanzado" },
  { id: 16, label: "Monotributo Trabajador Independiente Promovido" },
]);

export const ARCA_OPERATOR_ROLES = Object.freeze([
  { id: "owner", label: "Soy el titular o responsable" },
  { id: "accountant", label: "Lo hara mi contador" },
  { id: "authorized", label: "Otra persona autorizada" },
]);

export const FISCAL_CONTACT_LIMITS = Object.freeze({
  tradeName: 160, province: 80, contactEmail: 254, contactPhone: 40,
  operatorRole: 20, operatorName: 160,
});

export const normalizeFiscalContact = (value = {}) => Object.fromEntries(
  Object.keys(FISCAL_CONTACT_LIMITS).map((key) => [key, typeof value?.[key] === "string" ? value[key].trim() : ""])
);

export const validateFiscalContact = (value = {}) => {
  const contact = normalizeFiscalContact(value);
  if (Object.entries(FISCAL_CONTACT_LIMITS).some(([key, max]) => contact[key].length > max)) {
    return "Uno de los datos de contacto supera la longitud permitida.";
  }
  if (contact.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.contactEmail)) {
    return "Ingresa un correo fiscal o administrativo valido.";
  }
  if (contact.contactPhone && (!/^[+0-9 ()-]+$/.test(contact.contactPhone) || contact.contactPhone.replace(/\D/g, "").length < 6)) {
    return "Ingresa un telefono de contacto valido.";
  }
  if (contact.operatorRole && !ARCA_OPERATOR_ROLES.some((role) => role.id === contact.operatorRole)) {
    return "Selecciona quien realizara el tramite.";
  }
  if (Boolean(contact.operatorRole) !== Boolean(contact.operatorName)) {
    return "Completa el nombre y la funcion de la persona que realizara el tramite.";
  }
  return "";
};

export const hasCompleteFiscalContact = (value) => {
  const contact = normalizeFiscalContact(value);
  return !validateFiscalContact(contact) && Boolean(contact.operatorRole && contact.operatorName && (contact.contactEmail || contact.contactPhone));
};

export const FISCAL_DELEGATION_STATUS = Object.freeze({
  NOT_STARTED: "not_started",
  PENDING: "pending",
  VERIFIED: "verified",
  REVOKED: "revoked",
  ERROR: "error",
});

const FISCAL_DELEGATION_STATUS_OPTIONS = Object.freeze({
  [FISCAL_DELEGATION_STATUS.NOT_STARTED]: {
    label: "Sin iniciar",
    tone: "neutral",
    description: "Falta guardar los datos fiscales y completar la delegacion en ARCA.",
  },
  [FISCAL_DELEGATION_STATUS.PENDING]: {
    label: "Pendiente de revision",
    tone: "pending",
    description: "MozzoApp debe comprobar la delegacion y los puntos de venta.",
  },
  [FISCAL_DELEGATION_STATUS.VERIFIED]: {
    label: "Conexion verificada",
    tone: "success",
    description: "La identidad fiscal y la delegacion fueron validadas por MozzoApp.",
  },
  [FISCAL_DELEGATION_STATUS.REVOKED]: {
    label: "Conexion revocada",
    tone: "danger",
    description: "La delegacion dejo de estar activa y debe vincularse nuevamente.",
  },
  [FISCAL_DELEGATION_STATUS.ERROR]: {
    label: "Requiere correccion",
    tone: "danger",
    description: "MozzoApp encontro un dato o permiso que necesita ser corregido.",
  },
});

export const normalizeFiscalEnvironment = (value) =>
  value === "homologation" ? "homologation" : "production";

export const normalizeCuit = (value) => `${value || ""}`.replace(/\D/g, "").slice(0, 11);

export const formatCuit = (value) => {
  const cuit = normalizeCuit(value);

  if (cuit.length !== 11) {
    return cuit;
  }

  return `${cuit.slice(0, 2)}-${cuit.slice(2, 10)}-${cuit.slice(10)}`;
};

export const isValidCuit = (value) => {
  const cuit = `${value || ""}`.replace(/\D/g, "");

  if (!/^\d{11}$/.test(cuit)) {
    return false;
  }

  const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  const sum = weights.reduce(
    (total, weight, index) => total + Number(cuit[index]) * weight,
    0
  );
  const remainder = sum % 11;
  const expectedDigit = remainder === 0 ? 0 : remainder === 1 ? 9 : 11 - remainder;

  return expectedDigit === Number(cuit[10]);
};

export const normalizePointOfSale = (value) => {
  const parsedValue = Number.parseInt(`${value || ""}`.replace(/\D/g, ""), 10);

  return Number.isInteger(parsedValue) && parsedValue >= 1 && parsedValue <= 99998
    ? parsedValue
    : null;
};

export const getFiscalDelegationStatusOption = (value) =>
  FISCAL_DELEGATION_STATUS_OPTIONS[value] ||
  FISCAL_DELEGATION_STATUS_OPTIONS[FISCAL_DELEGATION_STATUS.NOT_STARTED];

export const createRestaurantFiscalDraft = ({ companyProfile = {}, issuer = null } = {}) => {
  const ivaCondition = ARCA_IVA_CONDITIONS.find(
    (condition) => condition.id === Number(issuer?.ivaConditionId)
  );

  return {
    environment: normalizeFiscalEnvironment(issuer?.environment),
    cuit: normalizeCuit(issuer?.cuit || companyProfile.taxId),
    legalName: issuer?.legalName || companyProfile.legalName || "",
    ivaConditionId: issuer?.ivaConditionId ? `${issuer.ivaConditionId}` : "",
    ivaConditionLabel: issuer?.ivaConditionLabel || ivaCondition?.label || "",
    grossIncomeNumber: issuer?.grossIncomeNumber || "",
    fiscalAddress: issuer?.fiscalAddress || companyProfile.headquartersAddress || "",
    ...normalizeFiscalContact(issuer?.metadata?.onboardingContact || {
      tradeName: companyProfile.companyName,
      contactEmail: companyProfile.billingEmail,
      contactPhone: companyProfile.companyPhone,
    }),
  };
};

export const validateRestaurantFiscalDraft = (draft = {}) => {
  if (!isValidCuit(draft.cuit)) {
    return "Ingresa un CUIT valido de 11 digitos.";
  }

  if (!`${draft.legalName || ""}`.trim()) {
    return "La razon social del emisor es obligatoria.";
  }

  if (!ARCA_IVA_CONDITIONS.some((condition) => condition.id === Number(draft.ivaConditionId))) {
    return "Selecciona la condicion frente al IVA.";
  }

  if (!`${draft.fiscalAddress || ""}`.trim()) {
    return "El domicilio fiscal es obligatorio.";
  }

  return "";
};

export const buildPointOfSaleDrafts = (branches = [], pointsOfSale = []) =>
  branches.reduce((result, branch) => {
    const savedPoint = pointsOfSale.find((point) => point.branchId === branch.id);

    result[branch.id] = {
      pointOfSale: savedPoint?.pointOfSale ? `${savedPoint.pointOfSale}` : "",
      label: savedPoint?.label || `MozzoApp - ${branch.name || "Sucursal"}`,
      verifiedAt: savedPoint?.verifiedAt || "",
    };

    return result;
  }, {});
